/**
 * Session-lived cache for the staff club/game pickers.
 *
 * The admin games listing is a real query against a remote database; paying
 * it on every tab open made the Insights Lab feel slow (Ben, 2026-09-01).
 * Club membership changes rarely, so the list is cached in module memory
 * and explicitly INVALIDATED where clubs change (Manage Clubs create /
 * delete) — plus a TTL backstop so a stale entry cannot outlive a session
 * that saw another admin's change.
 *
 * Per-org game lists get a short TTL only: games appear as pipelines finish,
 * and nothing in the SPA observes that, so a minute is the honest horizon.
 */
import { api } from './api'

export type CachedClub = {
  id: number
  code: string
  name: string
  games: number
  /** The club's badge. The admin listing has always returned it; it was
   *  simply not carried through the cache until the Lab showed badges. */
  logo_url: string | null
}

export type CachedGame = {
  game_code: string
  org_id: number
  date: string | null
}

/** A team code in one club's games, with its resolved badge. */
export type TeamBadge = { code: string; url: string | null }

type AdminGames = {
  games: { game_code: string; org_id: number; match_date: string | null }[]
  clubs: CachedClub[]
}

const CLUBS_TTL_MS = 10 * 60 * 1000
const GAMES_TTL_MS = 60 * 1000

let clubsEntry: { at: number; promise: Promise<CachedClub[]> } | null = null
const gamesEntries = new Map<number, { at: number; promise: Promise<CachedGame[]> }>()

async function fetchAdminGames(orgId?: number): Promise<AdminGames> {
  const suffix = orgId ? `?org=${orgId}&sort=date&dir=desc` : '?sort=date&dir=desc'
  return api<AdminGames>(`/api/v1/admin/games${suffix}`)
}

/** The staff club list, cached. Callers share one in-flight request. */
export function getClubs(): Promise<CachedClub[]> {
  const now = Date.now()
  if (!clubsEntry || now - clubsEntry.at > CLUBS_TTL_MS) {
    const promise = fetchAdminGames().then((d) => d.clubs)
    clubsEntry = { at: now, promise }
    // A failed fetch must not be cached as "the club list".
    promise.catch(() => { if (clubsEntry?.promise === promise) clubsEntry = null })
  }
  return clubsEntry.promise
}

/** One club's games, newest first, briefly cached. */
export function getClubGames(orgId: number): Promise<CachedGame[]> {
  const now = Date.now()
  const hit = gamesEntries.get(orgId)
  if (!hit || now - hit.at > GAMES_TTL_MS) {
    const promise = fetchAdminGames(orgId).then((d) =>
      d.games.map((g) => ({
        game_code: g.game_code, org_id: g.org_id, date: g.match_date,
      })))
    gamesEntries.set(orgId, { at: now, promise })
    promise.catch(() => {
      if (gamesEntries.get(orgId)?.promise === promise) gamesEntries.delete(orgId)
    })
  }
  return gamesEntries.get(orgId)!.promise
}

/** Warm the club list before anyone opens the picker. Fire-and-forget. */
export function prefetchClubs(): void {
  void getClubs().catch(() => undefined)
}

// Team badges, per club, cached for the session. One request when a club is
// opened covers every game it owns: the endpoint returns every team code
// appearing in that club's games, which is exactly both sides of every match.
const badgeEntries = new Map<number, Promise<Map<string, string | null>>>()

/** Call wherever clubs are created or deleted. */
export function invalidateClubsCache(): void {
  clubsEntry = null
  gamesEntries.clear()
  // Badges are keyed by club id; a deleted club must not leave its badges
  // behind for the next club to take that id.
  badgeEntries.clear()
}

export function getClubTeamBadges(orgId: number): Promise<Map<string, string | null>> {
  const hit = badgeEntries.get(orgId)
  if (hit) return hit
  const promise = api<{ teams: TeamBadge[] }>(
    `/api/v1/admin/clubs/${orgId}/team-logos`)
    .then((d) => new Map(d.teams.map((t) => [t.code.toUpperCase(), t.url])))
    // A club with no badges must still open; an empty map renders the code
    // placeholders, which is the same thing ClubLogo does for a broken URL.
    .catch(() => new Map<string, string | null>())
  badgeEntries.set(orgId, promise)
  return promise
}
